import React, { useEffect, useMemo, useState } from 'react';
import {
 Shield,
 X,
 AlertCircle,
 Copy,
 Check,
 Loader2,
 KeyRound,
 Download,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Button from './Button';
import Input from './Input';
import ModalShell from './ModalShell';
import { useToastContext } from '../../contexts/ToastContext';
import {
 getTwoFactorStatus,
 setupTwoFactor,
 confirmTwoFactor,
 cancelTwoFactorSetup,
 disableTwoFactor,
 regenerateTwoFactorRecoveryCodes,
} from '../../services/twoFactorService';

const TwoFactorAuthSettings = ({
 title = 'Two-Factor Authentication',
 description = 'Add an extra layer of security to your account with an authenticator app.',
 queryKey = 'two-factor-status',
 cardClassName = '',
}) => {
 const toast = useToastContext();
 const queryClient = useQueryClient();

 const [setupOpen, setSetupOpen] = useState(false);
 const [disableOpen, setDisableOpen] = useState(false);
 const [setupStep, setSetupStep] = useState('scan'); // scan | confirm | backup
 const [setupData, setSetupData] = useState(null);
 const [confirmCode, setConfirmCode] = useState('');
 const [backupCodes, setBackupCodes] = useState([]);
 const [copied, setCopied] = useState(false);
 const [disablePassword, setDisablePassword] = useState('');
 const [disableCode, setDisableCode] = useState('');
 const [regenPassword, setRegenPassword] = useState('');
 const [regenOpen, setRegenOpen] = useState(false);

 const statusQuery = useQuery({
 queryKey: [queryKey],
 queryFn: async () => getTwoFactorStatus(),
 });

 const enabled = Boolean(statusQuery.data?.data?.enabled);
 const recoveryRemaining = statusQuery.data?.data?.recovery_codes_remaining ?? 0;

 const setupMutation = useMutation({
 mutationFn: setupTwoFactor,
 onSuccess: (res) => {
 setSetupData(res?.data || null);
 setSetupStep('scan');
 setConfirmCode('');
 setBackupCodes([]);
 setSetupOpen(true);
 },
 onError: (err) => {
 toast.error('Setup failed', err?.response?.data?.message || err?.message || 'Could not start 2FA setup.');
 },
 });

 const confirmMutation = useMutation({
 mutationFn: (code) => confirmTwoFactor(code),
 onSuccess: (res) => {
 const codes = res?.data?.backup_codes || [];
 setBackupCodes(codes);
 setSetupStep('backup');
 queryClient.invalidateQueries({ queryKey: [queryKey] });
 toast.success('2FA enabled', res?.message || 'Two-factor authentication enabled.');
 },
 onError: (err) => {
 toast.error('Invalid code', err?.response?.data?.message || err?.message || 'Invalid code.');
 },
 });

 const cancelMutation = useMutation({
 mutationFn: cancelTwoFactorSetup,
 onSuccess: () => {
 setSetupOpen(false);
 setSetupData(null);
 setConfirmCode('');
 },
 });

 const disableMutation = useMutation({
 mutationFn: () =>
 disableTwoFactor({
 password: disablePassword,
 code: disableCode || undefined,
 }),
 onSuccess: (res) => {
 toast.success('2FA disabled', res?.message || 'Two-factor authentication disabled.');
 setDisableOpen(false);
 setDisablePassword('');
 setDisableCode('');
 queryClient.invalidateQueries({ queryKey: [queryKey] });
 },
 onError: (err) => {
 toast.error('Could not disable', err?.response?.data?.message || err?.message || 'Could not disable 2FA.');
 },
 });

 const regenMutation = useMutation({
 mutationFn: () => regenerateTwoFactorRecoveryCodes(regenPassword),
 onSuccess: (res) => {
 setBackupCodes(res?.data?.backup_codes || []);
 setRegenOpen(false);
 setRegenPassword('');
 setSetupOpen(true);
 setSetupStep('backup');
 queryClient.invalidateQueries({ queryKey: [queryKey] });
 toast.success('Codes generated', res?.message || 'New recovery codes generated.');
 },
 onError: (err) => {
 toast.error('Could not regenerate', err?.response?.data?.message || err?.message || 'Could not regenerate codes.');
 },
 });

 useEffect(() => {
 if (!copied) return undefined;
 const t = setTimeout(() => setCopied(false), 2000);
 return () => clearTimeout(t);
 }, [copied]);

 const qrSvgMarkup = useMemo(() => {
 const svg = setupData?.qr_code_svg;
 if (!svg || typeof svg !== 'string') return null;
 return { __html: svg };
 }, [setupData?.qr_code_svg]);

 const copySecret = async () => {
 if (!setupData?.secret) return;
 try {
 await navigator.clipboard.writeText(setupData.secret);
 setCopied(true);
 toast.success('Copied', 'Secret copied.');
 } catch {
 toast.error('Copy failed', 'Could not copy secret.');
 }
 };

 const copyBackupCodes = async () => {
 if (!backupCodes.length) return;
 try {
 await navigator.clipboard.writeText(backupCodes.join('\n'));
 setCopied(true);
 toast.success('Copied', 'Recovery codes copied.');
 } catch {
 toast.error('Copy failed', 'Could not copy codes.');
 }
 };

 const downloadBackupCodes = () => {
 const blob = new Blob(
 [`vendaList recovery codes\n\n${backupCodes.join('\n')}\n`],
 { type: 'text/plain' }
 );
 const url = URL.createObjectURL(blob);
 const a = document.createElement('a');
 a.href = url;
 a.download = 'vendalist-2fa-recovery-codes.txt';
 a.click();
 URL.revokeObjectURL(url);
 };

 const closeSetup = () => {
 if (setupStep === 'scan' || setupStep === 'confirm') {
 cancelMutation.mutate();
 }
 setSetupOpen(false);
 setSetupData(null);
 setConfirmCode('');
 if (setupStep === 'backup') {
 setBackupCodes([]);
 setSetupStep('scan');
 }
 };

 const finishBackup = () => {
 setSetupOpen(false);
 setSetupData(null);
 setBackupCodes([]);
 setSetupStep('scan');
 };

 return (
 <div className={`space-y-4 sm:space-y-6 ${cardClassName}`}>
 <div>
 <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
 {title}
 </h3>
 <p className="text-sm text-gray-600">{description}</p>
 </div>

 <div className="bg-surface-muted rounded-lg border border-gray-200 p-4 sm:p-6">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
 <div className="flex items-center">
 <div
 className={`w-3 h-3 rounded-full mr-3 flex-shrink-0 ${ enabled ? 'bg-emerald-500' : 'bg-gray-400' }`}
 />
 <div className="min-w-0">
 <h4 className="font-medium text-gray-900 text-sm sm:text-base">
 {statusQuery.isLoading
 ? 'Checking status…'
 : enabled
 ? 'Two-Factor Authentication Enabled'
 : 'Two-Factor Authentication Disabled'}
 </h4>
 <p className="text-xs sm:text-sm text-gray-600">
 {enabled
 ? `Your account requires an authenticator code at login · ${recoveryRemaining} recovery codes left`
 : 'Enable two-factor authentication for enhanced security'}
 </p>
 </div>
 </div>

 <div className="flex flex-wrap gap-2 w-full sm:w-auto">
 {enabled ? (
 <>
 <Button
 onClick={() => setRegenOpen(true)}
 variant="outline"
 size="sm"
 className="flex items-center justify-center flex-1 sm:flex-none"
 >
 <KeyRound className="w-4 h-4 mr-2" />
 Recovery codes
 </Button>
 <Button
 onClick={() => setDisableOpen(true)}
 variant="outline"
 size="sm"
 className="flex items-center justify-center flex-1 sm:flex-none"
 >
 <X className="w-4 h-4 mr-2" />
 Disable
 </Button>
 </>
 ) : (
 <Button
 onClick={() => setupMutation.mutate()}
 variant="primary"
 size="sm"
 disabled={setupMutation.isPending || statusQuery.isLoading}
 className="flex items-center justify-center w-full sm:w-auto"
 >
 {setupMutation.isPending ? (
 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
 ) : (
 <Shield className="w-4 h-4 mr-2" />
 )}
 Enable
 </Button>
 )}
 </div>
 </div>
 </div>

 <div className="bg-surface-muted border border-border rounded-lg p-4">
 <div className="flex">
 <AlertCircle className="h-5 w-5 text-ink-secondary mr-3 mt-0.5 flex-shrink-0" />
 <div className="min-w-0">
 <h4 className="text-sm font-medium text-ink">Security Tip</h4>
 <p className="text-sm text-ink mt-1">
 Use Google Authenticator, Authy, or 1Password. Store recovery codes offline — you will
 need them if you lose your phone.
 </p>
 </div>
 </div>
 </div>

 {/* Setup modal */}
 <ModalShell
 isOpen={setupOpen}
 onClose={setupStep === 'backup' ? finishBackup : closeSetup}
 panelClassName="max-w-lg"
 labelledBy="two-factor-setup-title"
 >
 <div className="bg-surface rounded-lg shadow-sm border border-gray-200 p-5 sm:p-6">
 <div className="flex items-start justify-between gap-3 mb-4">
 <div>
 <h2
 id="two-factor-setup-title"
 className="text-lg font-semibold text-gray-900"
 >
 {setupStep === 'backup' ? 'Save recovery codes' : 'Set up authenticator'}
 </h2>
 <p className="text-sm text-gray-500 mt-1">
 {setupStep === 'backup'
 ? 'These codes are shown once. Store them somewhere safe.'
 : 'Scan the QR code, then enter the 6-digit code from your app.'}
 </p>
 </div>
 <button
 type="button"
 onClick={setupStep === 'backup' ? finishBackup : closeSetup}
 className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100"
 >
 <X className="w-5 h-5" />
 </button>
 </div>

 {(setupStep === 'scan' || setupStep === 'confirm') && setupData && (
 <div className="space-y-4">
 <div className="flex justify-center">
 <div className="bg-surface p-3 rounded-xl border border-gray-200 shadow-sm">
 {qrSvgMarkup ? (
 <div
 className="w-[220px] h-[220px] [&_svg]:w-full [&_svg]:h-full"
 dangerouslySetInnerHTML={qrSvgMarkup}
 />
 ) : (
 <div className="w-[220px] h-[220px] flex items-center justify-center text-gray-400 text-sm">
 QR unavailable
 </div>
 )}
 </div>
 </div>

 <div>
 <p className="text-xs font-medium text-gray-500 mb-1.5">
 Or enter this key manually
 </p>
 <div className="flex items-center gap-2">
 <code className="flex-1 text-xs sm:text-sm font-mono bg-surface-muted border border-gray-200 rounded-xl px-3 py-2 break-all">
 {setupData.secret}
 </code>
 <button
 type="button"
 onClick={copySecret}
 className="p-2 rounded-xl border border-gray-200 hover:bg-surface-muted"
 title="Copy secret"
 >
 {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
 </button>
 </div>
 </div>

 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1.5">
 Authentication code
 </label>
 <Input
 value={confirmCode}
 onChange={(e) => setConfirmCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
 placeholder="000000"
 inputMode="numeric"
 autoComplete="one-time-code"
 className="tracking-widest text-center text-lg font-mono"
 />
 </div>

 <div className="flex flex-wrap gap-2 justify-end pt-1">
 <Button variant="outline" size="sm" onClick={closeSetup}>
 Cancel
 </Button>
 <Button
 variant="primary"
 size="sm"
 disabled={confirmCode.length < 6 || confirmMutation.isPending}
 onClick={() => confirmMutation.mutate(confirmCode)}
 >
 {confirmMutation.isPending ? (
 <Loader2 className="w-4 h-4 mr-2 animate-spin" />
 ) : null}
 Confirm & enable
 </Button>
 </div>
 </div>
 )}

 {setupStep === 'backup' && (
 <div className="space-y-4">
 <div className="grid grid-cols-2 gap-2">
 {backupCodes.map((code) => (
 <div
 key={code}
 className="font-mono text-sm bg-surface-muted border border-gray-200 px-3 py-2 rounded-xl text-center"
 >
 {code}
 </div>
 ))}
 </div>
 <div className="flex flex-wrap gap-2">
 <Button variant="outline" size="sm" onClick={copyBackupCodes}>
 <Copy className="w-4 h-4 mr-2" />
 Copy
 </Button>
 <Button variant="outline" size="sm" onClick={downloadBackupCodes}>
 <Download className="w-4 h-4 mr-2" />
 Download
 </Button>
 <Button variant="primary" size="sm" className="ml-auto" onClick={finishBackup}>
 I saved my codes
 </Button>
 </div>
 </div>
 )}
 </div>
 </ModalShell>

 {/* Disable modal */}
 <ModalShell
 isOpen={disableOpen}
 onClose={() => setDisableOpen(false)}
 panelClassName="max-w-md"
 labelledBy="two-factor-disable-title"
 >
 <div className="bg-surface rounded-lg shadow-sm border border-gray-200 p-5 sm:p-6 space-y-4">
 <h2
 id="two-factor-disable-title"
 className="text-lg font-semibold text-gray-900"
 >
 Disable two-factor authentication
 </h2>
 <p className="text-sm text-gray-500">
 Enter your account password to turn off 2FA. Optionally include a current authenticator
 or recovery code.
 </p>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1.5">
 Current password
 </label>
 <Input
 type="password"
 value={disablePassword}
 onChange={(e) => setDisablePassword(e.target.value)}
 autoComplete="current-password"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1.5">
 Authenticator / recovery code (optional)
 </label>
 <Input
 value={disableCode}
 onChange={(e) => setDisableCode(e.target.value)}
 placeholder="6-digit or recovery code"
 autoComplete="one-time-code"
 />
 </div>
 <div className="flex justify-end gap-2">
 <Button variant="outline" size="sm" onClick={() => setDisableOpen(false)}>
 Cancel
 </Button>
 <Button
 variant="primary"
 size="sm"
 disabled={!disablePassword || disableMutation.isPending}
 onClick={() => disableMutation.mutate()}
 >
 {disableMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
 Disable 2FA
 </Button>
 </div>
 </div>
 </ModalShell>

 {/* Regenerate recovery codes */}
 <ModalShell
 isOpen={regenOpen}
 onClose={() => setRegenOpen(false)}
 panelClassName="max-w-md"
 labelledBy="two-factor-regen-title"
 >
 <div className="bg-surface rounded-lg shadow-sm border border-gray-200 p-5 sm:p-6 space-y-4">
 <h2
 id="two-factor-regen-title"
 className="text-lg font-semibold text-gray-900"
 >
 Generate new recovery codes
 </h2>
 <p className="text-sm text-gray-500">
 This replaces your previous recovery codes. Enter your password to continue.
 </p>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1.5">
 Current password
 </label>
 <Input
 type="password"
 value={regenPassword}
 onChange={(e) => setRegenPassword(e.target.value)}
 autoComplete="current-password"
 />
 </div>
 <div className="flex justify-end gap-2">
 <Button variant="outline" size="sm" onClick={() => setRegenOpen(false)}>
 Cancel
 </Button>
 <Button
 variant="primary"
 size="sm"
 disabled={!regenPassword || regenMutation.isPending}
 onClick={() => regenMutation.mutate()}
 >
 {regenMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
 Generate
 </Button>
 </div>
 </div>
 </ModalShell>
 </div>
 );
};

export default TwoFactorAuthSettings;
